/**
 * #605 — the Papan host's dialog for an app's `papan.files.pickAndRead()` and
 * `pickAndSave()`:
 * Papan's picker PAGE (`/__handoff/<pid>`) in a same-origin iframe over the
 * app. The page does the work (it runs as the signed-in person, in the tenant
 * its URL names, and answers the request itself); this dialog only frames it,
 * learns when it is done, and closes. The app's own iframe never sees any of
 * it — the host relays only the resulting grant URLs.
 *
 * Rendered in a portal on `document.body`, like every modal here, with the
 * theme class on the CONTENT element (CLAUDE.md "Theming": never the backdrop).
 *
 * Focus stays in the picker while it is open: Tab past either end wraps back
 * into it, and an app that calls `focus()` on its own page behind the dialog
 * (no user activation needed) has it taken straight back — else the person's
 * next keys, their search or the save's name, would land in the app. The
 * relay returns focus to the app after.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader2 } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { getThemeClass, useTheme } from '@papan/core-ui/ui'
import { HANDOFF_FINISHED_MESSAGE, type HandoffPageStatus } from '@papan/types'
import { PAPAN_FRAME_ATTR } from './papan-input.js'

export type HandoffDialogOutcome = HandoffPageStatus | 'dismissed'

/** Marks the dialog's root — how the relay knows focus is still in it. */
export const HANDOFF_DIALOG_ATTR = 'data-handoff-dialog'

const PAGE_STATUSES: ReadonlySet<unknown> = new Set<HandoffPageStatus>(['answered', 'cancelled', 'closed'])

/** Focus taken back this many times within `CONTEST_WINDOW_MS` is an app
 *  fighting for it (hostile, or one that keeps its own field focused). */
const CONTEST_RECLAIMS = 3
const CONTEST_WINDOW_MS = 1_000

export function HandoffDialog({ pid, tenantId, kind, onDone, onContested }: {
  /** Null while the request is being registered: the dialog shows, loading. */
  pid: string | null
  /** What the picker is for — names the dialog for assistive tech. */
  kind: 'read' | 'save'
  /** The drive the picker opens in — the SPA's active tenant. */
  tenantId: string
  /** `answered` / `cancelled`: the page settled the request; `closed`: the
   *  person is done watching a save. `dismissed`: the dialog was closed around
   *  the page (backdrop, Escape). */
  onDone: (outcome: HandoffDialogOutcome) => void
  /** The app keeps taking focus back: its frame is to be hidden until the
   *  dialog closes — a hidden frame cannot take focus, and a fight would spin
   *  (thousands of reclaims a second) and still lose keys between them. */
  onContested?: () => void
}) {
  const { t } = useTranslation('files')
  const { settings } = useTheme()
  const frameRef = useRef<HTMLIFrameElement>(null)
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone
  const onContestedRef = useRef(onContested)
  onContestedRef.current = onContested
  // Until the picker page has loaded in the frame, the dialog says it is
  // coming — a blank box reads as nothing having happened.
  const [loadedPid, setLoadedPid] = useState<string | null>(null)
  const loading = !pid || loadedPid !== pid
  // While it loads there is no picker to hold the keyboard: the dialog takes
  // it, so Escape closes it and nothing typed lands in the app behind.
  const panelRef = useRef<HTMLDivElement>(null)
  // the focus-reclaim timers: cleared when the dialog goes, so a dismissed dialog never takes focus again (and a
  // timer never runs against a page that is gone — CI 2026-10-09: `document is not defined` after a test's teardown)
  const reclaimTimers = useRef(new Set<ReturnType<typeof setTimeout>>())
  const later = (fn: () => void, ms: number) => {
    const t = setTimeout(() => { reclaimTimers.current.delete(t); fn() }, ms)
    reclaimTimers.current.add(t)
  }
  useEffect(() => () => {
    for (const t of reclaimTimers.current) clearTimeout(t)
    reclaimTimers.current.clear()
  }, [])
  const noFrameYet = !pid
  useEffect(() => { if (noFrameYet) panelRef.current?.focus() }, [noFrameYet])

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      // Only the page in OUR frame, on our own origin, may say it is done.
      if (e.origin !== window.location.origin || e.source !== frameRef.current?.contentWindow) return
      const d = e.data as { type?: unknown; pid?: unknown; status?: unknown } | null
      if (!pid || !d || d.type !== HANDOFF_FINISHED_MESSAGE || d.pid !== pid || !PAGE_STATUSES.has(d.status)) return
      onDoneRef.current(d.status as HandoffPageStatus)
    }
    // Escape while focus is still on the host page (inside the frame, the page
    // handles its own Escape and reports back).
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDoneRef.current('dismissed') }
    window.addEventListener('message', onMessage)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('message', onMessage)
      window.removeEventListener('keydown', onKey)
    }
  }, [pid])

  // Focus the app takes from the picker is taken back. An app's `focus()` on
  // its own element moves focus into ITS frame without this page's
  // `activeElement` changing (it stays the picker frame) — what tells is the
  // picker's document losing focus while this page, frames and all, keeps it
  // (`document.hasFocus()`: false instead when the tab or window lost focus).
  // Only the picker's window hears it (`blur`); a tick later, once focus has
  // landed, it goes back to where it was inside the picker. The relay hands
  // focus back to the app only once this dialog is gone.
  // Left alone: focus on this dialog's OWN controls (the phone Close bar).
  // Anything else on the page is taken back from — an app can make Papan's
  // page behind the dialog take focus (a confirm it opens there, unseen under
  // the picker), and the person's keys must not go to what they cannot see.
  const holdFocus = () => {
    try {
      const win = frameRef.current?.contentWindow
      const doc = frameRef.current?.contentDocument
      if (!win || !doc) return
      let inside: HTMLElement | null = null
      doc.addEventListener('focusin', (e) => { inside = e.target as HTMLElement }, true)
      const reclaims: number[] = []
      const reclaim = () => {
        const frame = frameRef.current
        const active = document.activeElement
        if (!frame || !document.hasFocus() || doc.hasFocus()) return
        if (active && active !== frame && active.closest(`[${HANDOFF_DIALOG_ATTR}]`)) return
        ;(inside?.isConnected ? inside : win).focus()
        const now = Date.now()
        reclaims.push(now)
        while (reclaims.length && now - reclaims[0]! > CONTEST_WINDOW_MS) reclaims.shift()
        if (reclaims.length >= CONTEST_RECLAIMS) {
          reclaims.length = 0
          onContestedRef.current?.()
          // Hiding the app's frame drops the focus it held: take it once more.
          later(reclaim, 50)
        }
      }
      win.addEventListener('blur', () => { later(reclaim, 0) })
    } catch { /* the frame holds a page of another origin: nothing to hold */ }
  }

  // Tab past either end wraps back in: to the picker, or — while it loads —
  // to the dialog itself (never out to the page or app behind it).
  const holdTab = () => { (frameRef.current ?? panelRef.current)?.focus() }

  const src = pid ? `/t/${encodeURIComponent(tenantId)}/__handoff/${encodeURIComponent(pid)}` : null
  const label = t(kind === 'save' ? 'handoff.page.dialogSave' : 'handoff.page.dialogLabel')
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center" data-testid="handoff-dialog" {...{ [HANDOFF_DIALOG_ATTR]: '' }}>
      <div className="absolute inset-0 bg-black/50" onClick={() => onDoneRef.current('dismissed')} />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={`${getThemeClass(settings.theme.workspace)} relative flex flex-col outline-none themed-bg-secondary shadow-2xl border themed-border overflow-hidden w-full h-full sm:w-[820px] sm:max-w-[calc(100vw-2rem)] sm:h-[600px] sm:max-h-[90vh] sm:rounded-lg`}
      >
        <span tabIndex={0} aria-hidden="true" onFocus={holdTab} />
        {/* Full screen on a phone there is no backdrop to tap, nor an Escape
            key: a way out that does not depend on the page inside loading. */}
        <div className="sm:hidden flex justify-end border-b themed-border px-2 py-1 shrink-0">
          <button
            type="button"
            data-testid="handoff-dialog-close"
            onClick={() => onDoneRef.current('dismissed')}
            className="px-3 py-1 text-sm themed-text-secondary hover:themed-text rounded"
          >
            {t('handoff.page.close')}
          </button>
        </div>
        <div className="relative w-full flex-1 min-h-0">
          {src && (
            <iframe
              ref={frameRef}
              {...{ [PAPAN_FRAME_ATTR]: '' }}
              src={src}
              title={label}
              className="absolute inset-0 w-full h-full border-0"
              // Keyboard lands in the picker straight away, and stays there.
              onLoad={() => { setLoadedPid(pid); holdFocus(); frameRef.current?.focus() }}
            />
          )}
          {loading && (
            <div data-testid="handoff-dialog-loading" className="absolute inset-0 flex items-center justify-center gap-2 text-sm themed-text-muted themed-bg-secondary pointer-events-none">
              <Loader2 size={16} className="animate-spin" />
              {t('handoff.page.loading')}
            </div>
          )}
        </div>
        <span tabIndex={0} aria-hidden="true" onFocus={holdTab} />
      </div>
    </div>,
    document.body,
  )
}
